import viewItem from './event/tracking/view-item';
import viewCategory from './event/tracking/view-category';
import cartUpdate from './event/tracking/cart-update';
import contactFormSubmit from './event/tracking/contact-form-submit';
import checkout from './event/tracking/checkout';
import consent from './event/tracking/consent';
import newsletterDoi from './event/tracking/newsletter-doi';
import notifymeRestock from './event/tracking/notifyme-restock';
import customerUpdate from './event/customer/customer-update';
import resetPassword from './event/customer/reset-password';
import customerConsent from './event/customer/customer-consent';
import verifyEmail from './event/customer/verify-email';
import newsletterSubscriptionProgress from './event/customer/newsletter-subscription-progress';
import initTracker from './util/initTracker';
import type { CiaDependencies } from './types';
import { Logger } from '~~/helpers/logger';
import localizationDefinitions from '~~/i18n/localizationDefinitions';
import { i18nToLanguageAndCountry } from '~~/helpers/locales/i18nCurrentDetails';
import { JS_SDK_CONFIG } from '~~/constants/bloomreach';

function createCiaFetch(nuxtApp: ReturnType<typeof useNuxtApp>) {

  if (import.meta.client) {

    return $fetch;
  
  }

  const ssrEvent = nuxtApp.ssrContext?.event;

  if (!ssrEvent) {

    return $fetch;
  
  }

  const reqHeaders = ssrEvent.node.req.headers;
  const forwardedForHeader = reqHeaders['x-forwarded-for'];
  const clientIp = Array.isArray(forwardedForHeader)
    ? forwardedForHeader[0]
    : (forwardedForHeader || ssrEvent.node.req.socket?.remoteAddress || '');

  const baseHeaders: Record<string, string> = {
    'x-forwarded-for': String(clientIp),
    'user-agent': String(reqHeaders['user-agent'] || ''),
    referer: String(reqHeaders.referer || ''),
    origin: String(reqHeaders.origin || ''),
  };

  return (request: unknown, options: Record<string, unknown> = {}) => {

    const mergedHeaders = {
      ...(options.headers as Record<string, string> || {}),
      ...baseHeaders,
    };

    return $fetch(request as string, {
      ...options,
      headers: mergedHeaders,
    });
  
  };

}

const useCia = () => {

  const nuxtApp = useNuxtApp();
  const { $i18n } = nuxtApp;
  const languageAndCountry = i18nToLanguageAndCountry($i18n);

  const countryLocalization = localizationDefinitions.getByLanguageAndCountry(languageAndCountry);
  const bloomreachDetails = countryLocalization?.getBloomreachDetails(languageAndCountry);
  const jsSdkPublicKey = bloomreachDetails?.key || '';

  if (import.meta.dev && import.meta.client && !jsSdkPublicKey) {

    console.warn('[cia] Missing Bloomreach/Exponea JS SDK public key for locale:', languageAndCountry);
  
  }

  const ciaFetch = createCiaFetch(nuxtApp);

  const cookieGetter = {
    get: (name: string) => {

      const cookie = useCookie<string | string[] | null | undefined>(name);

      return cookie.value ?? undefined;
    
    },
  };

  const dependencies: CiaDependencies = {
    fetch: ciaFetch as typeof $fetch,
    cookies: cookieGetter,
    languageAndCountry,
  };

  const { ciaState } = useCiaState();

  const event = {
    viewItem: viewItem(dependencies),
    viewCategory: viewCategory(dependencies),
    cartUpdate: cartUpdate(dependencies),
    contactFormSubmit: contactFormSubmit(dependencies),
    customerUpdate: customerUpdate(dependencies),
    consent: consent(dependencies),
    customerConsent: customerConsent(dependencies),
    newsletterSubscriptionProgress: newsletterSubscriptionProgress(dependencies),
    newsletterDOI: newsletterDoi(dependencies),
    notifymeRestock: notifymeRestock(dependencies),
    checkout: checkout(dependencies),
    resetPassword: resetPassword(dependencies),
    verifyEmail: verifyEmail(dependencies),
  };

  const util = {
    initTracker: () => initTracker(jsSdkPublicKey, JS_SDK_CONFIG),
  };

  const registerCheckoutEvent = (checkoutStep: number) => {

    try {

      event.checkout({
        step: checkoutStep,
        cartId: ciaState.value.cartId,
        email: ciaState.value.email,
        userId: ciaState.value.userId,
      });
    
    } catch (error) {

      Logger.error(`cia|checkout error: ${error}`);
    
    }
  
  };

  return {
    event,
    util,
    registerCheckoutEvent,
  };

};

export default useCia;
